import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [100, 500, 1000, 2000];

export default function PaymentLink() {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const [link, setLink] = useState(null);
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/public/payment-links/${linkId}`)
      .then((r) => setLink(r.data))
      .catch(() => setLink(false));
  }, [linkId]);

  const pay = async () => {
    const amountInApp = link && link.amount_in_app;
    if (!amountInApp) {
      const amt = parseFloat(amount);
      if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    }
    setSubmitting(true);
    try {
      const payload = amountInApp ? {} : { amount: parseFloat(amount) };
      const { data } = await api.post(`/public/payment-links/${linkId}/order`, payload);
      navigate(`/pay/${data.order_id}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setSubmitting(false);
    }
  };

  if (link === null)
    return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const invalid = link === false || !link.active;
  const presetList = link && link.presets && link.presets.length ? link.presets : PRESETS;

  return (
    <div className="min-h-screen bg-background grain-overlay">
      <header className="border-b bg-card/60 glass">
        <div className="mx-auto max-w-md flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary grid place-items-center"><Zap className="h-4 w-4 text-primary-foreground" fill="currentColor" /></div>
            <span className="font-heading font-bold">WoltPe</span>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <div className="mx-auto max-w-md px-4 py-6">
        <Card className="overflow-hidden animate-fade-up">
          {invalid ? (
            <div className="p-8 text-center" data-testid="pl-invalid">
              <XCircle className="h-10 w-10 mx-auto text-destructive" />
              <h1 className="mt-3 font-heading text-xl font-bold">Link unavailable</h1>
              <p className="text-sm text-muted-foreground mt-1">This payment link is invalid or has been paused.</p>
            </div>
          ) : (
            <>
              <div className="bg-primary/[0.06] border-b p-5 text-center">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Paying to</p>
                <p className="mt-1 font-heading text-lg font-bold">{link.merchant_name}</p>
                <p className="mt-1 text-xs text-muted-foreground">{link.note}</p>
              </div>
              {link.amount_in_app ? (
                <div className="p-5 space-y-4" data-testid="pl-pay-in-app">
                  <p className="text-sm text-muted-foreground text-center">
                    Tap below to open the payment — you'll enter the amount directly in your UPI app.
                  </p>
                  <Button className="w-full" onClick={pay} disabled={submitting} data-testid="pl-continue">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pay now"}
                  </Button>
                  <p className="text-[11px] text-center text-muted-foreground">
                    Pay the exact amount you type in your UPI app — no extra paise is added.
                  </p>
                </div>
              ) : (
                <div className="p-5 space-y-4" data-testid="pl-amount-entry">
                  <div className="space-y-2">
                    <Label htmlFor="pl-amt">Enter the amount you want to pay (₹)</Label>
                    <Input id="pl-amt" type="number" min="1" step="0.01" value={amount}
                      onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 500"
                      className="font-mono text-lg" inputMode="decimal" data-testid="pl-amount-input"
                      onKeyDown={(e) => { if (e.key === "Enter") pay(); }} />
                    <div className="flex flex-wrap gap-2" data-testid="pl-amount-presets">
                      {presetList.map((p) => (
                        <button type="button" key={p} onClick={() => setAmount(String(p))}
                          data-testid={`pl-preset-${p}`}
                          className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${String(p) === amount ? "border-primary bg-primary/10 text-primary" : "hover:bg-muted"}`}>
                          ₹{p.toLocaleString("en-IN")}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Button className="w-full" onClick={pay} disabled={submitting} data-testid="pl-continue">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue to pay"}
                  </Button>
                  <p className="text-[11px] text-center text-muted-foreground">
                    Pay the exact amount you enter — no extra paise is added.
                  </p>
                </div>
              )}
            </>
          )}
        </Card>
        <p className="mt-4 text-center text-xs text-muted-foreground font-mono">Secured by WoltPe · TLS encrypted</p>
      </div>
    </div>
  );
}
